<template>
  <div class="min-h-full">
    <div
      class="mb-4 flex flex-col items-center justify-between gap-8 py-2 md:flex-row-reverse"
    >
      <div
        class="flex flex-col items-center justify-between gap-2 md:flex-row"
      >
        <ReportDeviceFilter v-model="filters.deviceId" />
        <DateFilter
          :model-value="globalQuery"
          :time-period-label="timePeriodLabel"
          range-type="custom"
          @update:model-value="setGlobalQuery"
        />
      </div>

      <SearchInput v-model="searchRaw" />
    </div>

    <div>
      <template v-if="data">
        <template v-if="data.items.length > 0">
          <div class="mb-3">
            <template
              v-for="[
                date,
                reports,
              ] in reportsGroupedByDate"
              :key="date"
            >
              <div class="px-2 py-3 text-sm">
                {{ date }}
              </div>
              <ol class="-mx-2 mb-6 space-y-1 py-px">
                <li
                  v-for="report in reports"
                  :key="report.id"
                >
                  <RouterLink
                    :to="{
                      name: 'zReportDetail',
                      params: { zReportId: report.id },
                    }"
                  >
                    <ZReportsIndexListItem
                      :report="report"
                    />
                  </RouterLink>
                </li>
              </ol>
            </template>
          </div>
          <div class="mb-4 flex justify-around px-8">
            <BasePagination
              v-model:page="page"
              :page-count="pageCount"
            />
          </div>
        </template>

        <template v-else>
          <p
            class="w-full px-8 py-4 text-center text-background-fg/70"
          >
            No Z reports to display
          </p>
        </template>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import type {
  ListZReportsResponseItem,
  ZReportQuery,
} from "@/services/zReports/listZReports"
import { useZReports } from "@/services/zReports/listZReports"

const LIMIT = 20

const filters = reactive({
  deviceId: undefined as number | undefined,
})

const searchRaw = ref<string>("")

const { globalQuery, setGlobalQuery, timePeriodLabel } =
  useReportQuery({})

const search = refDebounced(searchRaw, 750)

const {
  page,
  pageCount,
  paginationQuery,
  resetPagination,
} = usePagination({
  count: computed(() => data.value?.count ?? 0),
  limit: LIMIT,
})

watch(
  () => [filters.deviceId, globalQuery.value, search.value],
  () => resetPagination(),
  { deep: true, immediate: true }
)

const query = reactiveComputed<ZReportQuery>(() => ({
  deviceId: filters.deviceId,
  ...globalQuery.value,
  search: search.value?.trim() || undefined,
  ...paginationQuery.value,
}))

const { data } = useZReports(query)

const reportsGroupedByDate = computed(() => {
  if (!data.value)
    return new Map<string, ListZReportsResponseItem[]>()

  const groupedMap = new Map<
    string,
    ListZReportsResponseItem[]
  >()

  for (const report of data.value.items) {
    const datePart = formatToDate(report.createdAt)
    const existing = groupedMap.get(datePart) ?? []
    groupedMap.set(datePart, [...existing, report])
  }

  return groupedMap
})
</script>
